/**
 * Which payment methods this desk offers, as the Settings panel reads them.
 *
 * Mirrors `apps/api/src/modules/settings/crm-payments.settings.ts` (the round
 * trip) over the vocabulary in `@wa/core/payment-methods` (the shape). Nothing
 * here re-implements a rule: the server re-derives every custom row's id from
 * its own label on the way in, so what comes back from a save is what was
 * actually stored rather than what the panel hoped it sent.
 *
 * The list has two readers in this app - the Settings panel that edits it, and
 * the payment dialog on an invoice that offers it as a picker - which is why it
 * is a lib module rather than state inside the panel.
 */
import {
  type CrmPaymentMethods,
  type PaymentMethodRow,
  parsePaymentMethods,
} from "@wa/core/payment-methods";
import { api } from "./api";

export type { CrmPaymentMethods, PaymentMethodRow };

/**
 * `view:crm` on the server - wider than the write, because the payment dialog on
 * every invoice offers this list to anybody who may record a payment.
 *
 * Named `...Settings` and not `getPaymentMethods`, which `lib/payments.ts`
 * already owns. That one lists the DISTINCT method strings actually present in
 * history, so the payments screen can filter by them; this one is the desk's
 * configuration. Two routes, two questions, and a payment booked under a method
 * later switched off must still be findable by the other.
 */
export const getPaymentMethodSettings = async (): Promise<CrmPaymentMethods> =>
  parsePaymentMethods(await api.get<unknown>("/admin/crm/payment-settings"));

/** A whole-list write. `manage:crm.sales` on the server, and a REPLACE: a custom
 *  row that has been removed is an absence, and a merge cannot see one. */
export const putPaymentMethodSettings = async (next: CrmPaymentMethods): Promise<CrmPaymentMethods> =>
  parsePaymentMethods(await api.put<unknown>("/admin/crm/payment-settings", next));

/**
 * The list, for a screen that only wants to READ it.
 *
 * Falls back to the default rather than throwing, because the caller is the
 * payment dialog: a settings read that failed must not stop an operator
 * recording money that has arrived. The dialog keeps its escape hatch for
 * exactly the same reason.
 *
 * A rejected promise is not the only way a read fails. Both functions above run
 * the response through `parsePaymentMethods`, so a 200 carrying the wrong shape
 * - a stub, a proxy's error page, a route that answered `{ok:true}` - becomes
 * the default list instead of an object with no `rows` for the first
 * `.filter()` to throw on.
 */
export async function readPaymentMethodsOrDefault(): Promise<CrmPaymentMethods> {
  try {
    return await getPaymentMethodSettings();
  } catch {
    return parsePaymentMethods(undefined);
  }
}

/**
 * Are these two lists the same decision?
 *
 * Positional, because the order is part of the decision - it picks what the
 * payment dialog prefills - so a list reordered and nothing else is a dirty
 * form. Used to tell a dirty panel from a clean one, since this one saves on a
 * button rather than on every tick.
 */
export function paymentMethodsEqual(a: CrmPaymentMethods, b: CrmPaymentMethods): boolean {
  if (a.rows.length !== b.rows.length) return false;
  return a.rows.every((row, i) => {
    const other = b.rows[i]!;
    return row.id === other.id && row.label === other.label && row.enabled === other.enabled;
  });
}
